import React from "react";
import { ArrowLeft, ShieldCheck, Lock, Cookie, BarChart3, Mail } from "lucide-react";

const LOGO_URL = "/logo.png";

const PrivacyPolicy = () => {
  const lastUpdated = "May 26, 2026";

  return (
    <div className="min-h-screen bg-amber-50/30" data-testid="privacy-page">
      {/* Header */}
      <header className="bg-white border-b border-amber-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img src={LOGO_URL} alt="Pathways to Prosperity" className="h-12 w-12 object-contain" />
            <span className="font-display font-bold text-gray-900">Pathways to Prosperity, LLC</span>
          </a>
          <a href="/" className="text-emerald-700 hover:underline inline-flex items-center gap-1 text-sm">
            <ArrowLeft size={16} /> Back to Home
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-12">
        {/* Privacy First Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-full mb-6">
          <ShieldCheck className="text-emerald-700" size={18} />
          <span className="text-emerald-700 font-semibold text-sm">Privacy First</span>
        </div>

        <h1 className="font-display text-4xl sm:text-5xl font-bold text-gray-900 mb-3">Privacy Policy</h1>
        <p className="text-gray-500 mb-10">Last updated: {lastUpdated}</p>

        <div className="prose prose-emerald max-w-none space-y-8">
          {/* Summary */}
          <section className="bg-white rounded-2xl border border-amber-100 p-6">
            <h2 className="font-display text-xl font-bold text-gray-900 mb-3">In Plain English</h2>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>We don't sell, trade, or rent your information — ever.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>We don't use Google Analytics, Facebook Pixel, or third-party tracking.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>We collect anonymous page-view counts to learn which content helps families most.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Information you submit via forms is used only to provide our services to you.</span>
              </li>
            </ul>
          </section>

          {/* Who we are */}
          <section>
            <h2 className="font-display text-2xl font-bold text-gray-900 mb-3">1. Who We Are</h2>
            <p className="text-gray-700 leading-relaxed">
              Pathways to Prosperity, LLC (also known as "Auntie Education") is an educational counseling
              service founded by Sheila Coleman-Castells, M.Ed., based in the United States. We provide
              college admissions, scholarship, and workforce-training guidance to students and families
              across the country.
            </p>
          </section>

          {/* Information we collect */}
          <section>
            <h2 className="font-display text-2xl font-bold text-gray-900 mb-3">2. Information We Collect</h2>

            <h3 className="font-semibold text-gray-900 mt-4 mb-2">a) Information You Voluntarily Provide</h3>
            <p className="text-gray-700 leading-relaxed mb-2">When you submit our Contact or Family Intake forms, you may share:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-1 ml-2">
              <li>Student name, parent/guardian name, address, email, phone</li>
              <li>High school, graduation year, test scores, grades</li>
              <li>Information about extracurriculars, family situation, educational goals</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-3">
              We use this information solely to respond to your inquiry, schedule consultations, and provide
              our counseling services. We never share it with third parties for marketing.
            </p>

            <h3 className="font-semibold text-gray-900 mt-6 mb-2">b) Automatic, Anonymous Information</h3>
            <p className="text-gray-700 leading-relaxed mb-2">
              When you visit our website, we automatically log the following — but <strong>we do not store
              your IP address, name, or any personal identifier</strong>:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-1 ml-2">
              <li>The page you viewed (e.g., "/services")</li>
              <li>The country your visit originated from (looked up at the moment of visit, not stored)</li>
              <li>The general source of your visit (e.g., "Facebook", "Google", "Direct")</li>
              <li>Whether you visited from a phone, tablet, or desktop</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-3">
              This data is aggregated to help us understand which pages are most useful. It cannot be
              traced back to you individually.
            </p>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="font-display text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Cookie className="text-amber-600" size={24} /> 3. Cookies
            </h2>
            <p className="text-gray-700 leading-relaxed mb-3">We use the absolute minimum number of cookies:</p>
            <div className="bg-white rounded-xl border border-amber-100 p-4 mb-3">
              <p className="font-semibold text-gray-900 mb-1">access_token</p>
              <p className="text-sm text-gray-600">
                Required for Sheila to log into the admin dashboard. HttpOnly + Secure. Expires in 24 hours.
                Set only when Sheila logs in — never on visitor browsers.
              </p>
            </div>
            <div className="bg-white rounded-xl border border-amber-100 p-4">
              <p className="font-semibold text-gray-900 mb-1">p2p_cookie_consent_v1 (browser storage)</p>
              <p className="text-sm text-gray-600">
                Remembers whether you've already seen our cookie notice so we don't show it again.
                Stays on your device only.
              </p>
            </div>
            <p className="text-gray-700 leading-relaxed mt-3">
              We do <strong>not</strong> use advertising cookies, tracking pixels, or third-party
              analytics scripts.
            </p>
          </section>

          {/* How we use info */}
          <section>
            <h2 className="font-display text-2xl font-bold text-gray-900 mb-3">4. How We Use Your Information</h2>
            <ul className="list-disc list-inside text-gray-700 space-y-1 ml-2">
              <li>To respond to your inquiry and schedule consultations</li>
              <li>To provide personalized college and career counseling</li>
              <li>To send you transactional emails (confirmations, follow-ups) — never marketing spam</li>
              <li>To understand which website content is most helpful, in aggregate</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          {/* Sharing */}
          <section>
            <h2 className="font-display text-2xl font-bold text-gray-900 mb-3">5. Information Sharing</h2>
            <p className="text-gray-700 leading-relaxed mb-2">
              We do <strong>not</strong> sell, trade, or rent your personal information. We share information
              only with these service providers, strictly to operate our site:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-1 ml-2">
              <li><strong>Resend</strong> — to send you confirmation and notification emails</li>
              <li><strong>MongoDB / Emergent Hosting</strong> — to securely store form submissions</li>
              <li><strong>Google Calendar</strong> — only if you use our scheduling page (handled by Google directly)</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-3">
              We may also disclose information if required by law, court order, or to protect rights and safety.
            </p>
          </section>

          {/* Your rights */}
          <section>
            <h2 className="font-display text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Lock className="text-emerald-700" size={24} /> 6. Your Rights
            </h2>
            <p className="text-gray-700 leading-relaxed mb-2">
              You have the right to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-1 ml-2">
              <li>Request a copy of the information we hold about you</li>
              <li>Request that we correct or delete your information</li>
              <li>Opt out of any communications from us at any time</li>
              <li>Withdraw consent to our processing of your information</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-3">
              Residents of the European Union (GDPR), United Kingdom, California (CCPA), and other
              jurisdictions with similar laws have additional rights. To exercise any right, please
              email us at the address below.
            </p>
          </section>

          {/* Data retention */}
          <section>
            <h2 className="font-display text-2xl font-bold text-gray-900 mb-3">7. Data Retention</h2>
            <ul className="list-disc list-inside text-gray-700 space-y-1 ml-2">
              <li><strong>Form submissions</strong> — kept while you are an active client + 3 years for record-keeping, then deleted on request.</li>
              <li><strong>Anonymous page views</strong> — kept indefinitely in aggregate; never tied to you.</li>
              <li><strong>Admin cookie</strong> — 24 hours.</li>
            </ul>
          </section>

          {/* Children */}
          <section>
            <h2 className="font-display text-2xl font-bold text-gray-900 mb-3">8. Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              Our services are designed for high-school and college-age students. Where students are under
              13, we require a parent/guardian to submit forms on their behalf. We do not knowingly
              collect personal information from children under 13 without parental involvement.
            </p>
          </section>

          {/* Security */}
          <section>
            <h2 className="font-display text-2xl font-bold text-gray-900 mb-3">9. Security</h2>
            <p className="text-gray-700 leading-relaxed">
              We use industry-standard security including TLS/HTTPS encryption in transit, HttpOnly
              cookies for authentication, bcrypt-hashed admin passwords, and access-controlled databases.
              No system is 100% secure, but we take reasonable precautions to protect your information.
            </p>
          </section>

          {/* Changes */}
          <section>
            <h2 className="font-display text-2xl font-bold text-gray-900 mb-3">10. Changes to This Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy from time to time. When we do, we'll update the "Last
              updated" date at the top of this page. For material changes, we'll display a notice on
              the homepage.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
            <h2 className="font-display text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Mail className="text-emerald-700" size={22} /> Contact Us
            </h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Questions, concerns, or requests about your privacy?
            </p>
            <p className="text-gray-700">
              <strong>Email:</strong>{" "}
              <a href="mailto:sheila@mypath2prosperity.com" className="text-emerald-700 font-bold italic hover:underline">
                <em>Sheila@MyPath2Prosperity.com</em>
              </a>
            </p>
            <p className="text-gray-700 mt-1">
              <strong>Phone:</strong>{" "}
              <a href="tel:+18447286843" className="text-emerald-700 font-semibold hover:underline">
                1-844-7-AUNTIE (1-844-728-6843)
              </a>
            </p>
          </section>

          {/* Privacy commitment */}
          <section className="text-center pt-8 border-t border-gray-200">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-2xl border border-emerald-200">
              <BarChart3 className="text-emerald-700" size={22} />
              <p className="text-gray-700 italic">
                "We believe families deserve trustworthy guidance — including with their data."
              </p>
            </div>
          </section>
        </div>
      </main>

      <footer className="border-t border-amber-100 mt-12 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm text-gray-500">
          © {new Date().getFullYear()} Pathways to Prosperity, LLC. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
